import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCreativeLibraryStore } from "../../store";
import { duplicateAdset, publishCreative, updatePublishCreative } from "../../apiService";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuCheck, LuTriangleAlert } from "react-icons/lu";
import { ArrowLeft, Check, Copy, Globe2, Inspect, Rocket, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";
import { AdsetAsset, CampaignAsset, PublishAdCreative } from "../../types";
import { BaseActionProps } from "@/components/common/adCard/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildRoute } from "@/router/constants";
import { useGlobalStore } from "@/GlobalStore";
import { AccountStatus } from "@/types";
import { Portal } from "@radix-ui/react-tooltip";

interface PublishActionProps extends BaseActionProps {
  published: boolean;
  creativeId: string;
  onPublish?: (publishAdCreative: PublishAdCreative) => void;
  update?: boolean;
  onUpdate?: (publishAdCreative: PublishAdCreative) => void;
  trigger?: React.ReactNode | ((props: { isPublishing: boolean }) => React.ReactNode);
  statusMessage?: string;
}

const PublishAction = ({
  published,
  creativeId,
  onPublish,
  update,
  onUpdate,
  trigger,
  statusMessage,
  forcedVisibility,
}: PublishActionProps) => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const accountAssets = useCreativeLibraryStore((s) => s.accountAssets);
  const loadingAccountAssets = useCreativeLibraryStore((s) => s.loadingAccountAssets);
  const lastUsedAsset = useCreativeLibraryStore((s) => s.lastUsedAsset);
  const setLastUsedAsset = useCreativeLibraryStore((s) => s.setLastUsedAsset);
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const [open, setOpen] = useState<boolean>(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedAsset, setSelectedAsset] = useState<{
    campaign: CampaignAsset | null;
    adset: AdsetAsset | null;
  }>({
    campaign: null,
    adset: null,
  });

  const [selectedAssetToDuplicate, setSelectedAssetToDuplicate] = useState<{
    campaign: CampaignAsset | null;
    adset: AdsetAsset | null;
  }>({
    campaign: null,
    adset: null,
  });

  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [selectAssetType, setSelectAssetType] = useState<"campaign" | "adset">(
    "campaign",
  );

  useEffect(() => {
    if (
      statusMessage &&
      ["Processing", "Approval Ready", "Published"].includes(statusMessage)
    ) {
      forcedVisibility?.(true);
    }
  }, [statusMessage, forcedVisibility]);

  const resetSelection = () => {
    setSelectedAsset({
      campaign: null,
      adset: null,
    });
    setSelectAssetType("campaign");
    setSearchQuery("");
  };

  const handleCampaignSelection = (campaign: CampaignAsset) => {
    setSelectedAsset((prev) => ({
      ...prev,
      campaign,
      adset: null, // Reset adset when campaign changes
    }));
    setSelectAssetType("adset");
    setSearchQuery(""); // Reset search query for adset selection
  };

  const handleAdsetSelection = async (adset: AdsetAsset) => {
    if (!accountId) return;
    setOpen(false);
    setIsPublishing(true);
    setSelectedAsset((prev) => ({
      ...prev,
      adset,
    }));

    try {
      let response;
      if (update) {
        response = await updatePublishCreative(accountId, creativeId, adset.id);
        if (onUpdate && response?.data) onUpdate(response.data);
      } else {
        response = await publishCreative(accountId, creativeId, adset.id);
        if (onPublish && response?.data) onPublish(response.data);
      }

      if (selectedAsset.campaign) {
        setLastUsedAsset({
          campaign: selectedAsset.campaign,
          adset: adset,
        });
      }
      if (response.status === "failed") {
        throw new Error(
          response?.message ?? `Failed to ${update ? "update" : "publish"} creative`,
        );
      }
    } catch (error) {
      console.error("Error publishing creative:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const expiredAccount = selectedAccount?.status === AccountStatus.EXPIRED;

  return (
    <TooltipProvider>
      <DropdownMenu
        open={open}
        onOpenChange={(open) => {
          // handling Approval Ready onClick
          if (statusMessage === "Approval Ready") {
            navigate(buildRoute.publish(accountId ?? ""));
            return;
          }
          forcedVisibility?.(open); // force visibility if open
          setOpen(open);
          if (!open) setTimeout(resetSelection, 100);
        }}
      >
        <Tooltip>
          <TooltipTrigger>
            <DropdownMenuTrigger asChild>
              {trigger ? (
                typeof trigger === "function" ? (
                  trigger({ isPublishing })
                ) : (
                  trigger
                )
              ) : (
                <Button
                  className={cn(
                    "pointer-events-auto bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-[#fff] p-[6px] rounded-[8px] cursor-pointer h-[30px] gap-1 flex items-center py-2 px-3 border-0",
                    statusMessage === "Published" &&
                      "disabled:bg-[#00000099] disabled:opacity-100",
                  )}
                  // not disabling trigger for Approval Ready
                  disabled={
                    expiredAccount ||
                    ((published || isPublishing) && statusMessage !== "Approval Ready")
                  }
                >
                  {isPublishing ? (
                    <>
                      <LoadingSpinner size={16} className="animate-spin" />
                      <span className="relative top-[1px]">Processing</span>
                    </>
                  ) : (
                    <>
                      {statusMessage === "Processing" ? (
                        <LoadingSpinner size={16} className="animate-spin" />
                      ) : statusMessage === "Approval Ready" ? (
                        <Check size={16} />
                      ) : statusMessage === "Published" ? (
                        <Globe2 size={16} />
                      ) : (
                        <Rocket size={16} />
                      )}
                      <span className="relative top-[1px]">
                        {!published ? "Publish" : statusMessage}
                      </span>
                    </>
                  )}
                </Button>
              )}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <Portal>
            <TooltipContent
              hidden={!expiredAccount}
              side="bottom"
              align="start"
              alignOffset={20}
            >
              <p>Access expired. Reconnect to publish.</p>
            </TooltipContent>
          </Portal>
        </Tooltip>

        <DropdownMenuContent
          side="right"
          align="start"
          avoidCollisions
          collisionPadding={10}
          className="min-w-[352px] w-[352px] rounded-[12px]"
        >
          <div className="text-brand-primary-800 text-[12px] font-medium leading-[19.16px] tracking-[0.02em] px-2 py-1.5">
            Select Campaign & Adset to Publish
          </div>

          {!update && lastUsedAsset ? (
            <div className="">
              <div
                className="flex items-center justify-between border-b border-t border-border-gray p-1 cursor-pointer"
                onClick={() => void handleAdsetSelection(lastUsedAsset.adset)}
              >
                <div>
                  <div>
                    <span className="font-medium text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new">
                      Campaign:{" "}
                    </span>
                    <span className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-all whitespace-normal">
                      {lastUsedAsset.campaign.name}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new">
                      Adset:{" "}
                    </span>
                    <span className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-all whitespace-normal">
                      {lastUsedAsset.adset.name}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-center text-brand-primary-800 whitespace-nowrap bg-[#0218590F] rounded-[4px] px-2 py-1 ml-8 h-6 font-normal text-[11px] leading-[18px] tracking-[0.02em]">
                  Last used
                </div>
              </div>
              <Button
                variant={"ghost"}
                className="hover:bg-[#fff] gap-2 px-2 w-full justify-start"
                onClick={() => {
                  resetSelection();
                  setLastUsedAsset(null);
                }}
              >
                <Inspect size={16} color="#334478" />
                <span className="text-brand-primary-800 font-normal text-[12px] leading-[19.16px] tracking-[0.02em] flex items-center">
                  Choose Another
                </span>
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-2 py-2 border-b border-t border-border-gray mb-1">
                <Search
                  size={16}
                  strokeWidth={1.5}
                  color="#02185999"
                  className="shrink-0"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={`Search ${selectAssetType}`}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full bg-transparent outline-none placeholder:text-[#02185999] text-brand-primary-new font-['Helvetica_Neue'] text-[13px] font-normal leading-[20px]"
                />
                {"adset" === selectAssetType && (
                  <button
                    className="h-5 flex items-center gap-1"
                    onClick={resetSelection}
                  >
                    <ArrowLeft size={16} color="#FF4500" />
                    <span className="text-[#02185999] text-[12px] font-normal leading-[20px] relative top-[1px]">
                      Back
                    </span>
                  </button>
                )}
              </div>
              {loadingAccountAssets && (
                <div className="h-[120px] flex items-center justify-center">
                  <LoadingSpinner size={24} color="#FF4500" />
                </div>
              )}
              {!loadingAccountAssets && "campaign" === selectAssetType && (
                <div className="max-h-[300px] overflow-y-auto">
                  {accountAssets &&
                    accountAssets.campaigns
                      ?.filter((campaign) =>
                        campaign.name
                          .toLowerCase()
                          .includes(searchQuery.trim().toLowerCase()),
                      )
                      .map((asset) => (
                        <DropdownMenuItem
                          key={asset.id}
                          onClick={(e) => {
                            e.preventDefault();
                            handleCampaignSelection(asset);
                          }}
                          className="break-words whitespace-normal text-wrap overflow-hidden"
                        >
                          <div className="whitespace-pre-wrap break-all font-['Helvetica_Neue'] font-normal text-[12px] leading-[19.16px] tracking-[2%] text-brand-primary-800">
                            {asset.name}
                          </div>
                        </DropdownMenuItem>
                      ))}
                </div>
              )}
              {!loadingAccountAssets && accountAssets && "adset" === selectAssetType && (
                <div className="max-h-[300px] overflow-y-auto">
                  {accountAssets?.adsets
                    .filter(
                      (adset) =>
                        adset.campaign_id === selectedAsset.campaign?.id &&
                        adset.name
                          .toLowerCase()
                          .includes(searchQuery.trim().toLowerCase()),
                    )
                    .map((adset) => (
                      <DropdownMenuItem
                        key={adset.id}
                        onClick={(e) => {
                          e.preventDefault();
                          void handleAdsetSelection(adset);
                        }}
                        className="group hover:bg-[#f6f6f6] flex items-center justify-between break-words whitespace-normal text-wrap overflow-hidden"
                      >
                        <div className="whitespace-pre-wrap break-all font-['Helvetica_Neue'] font-normal text-[12px] leading-[19.16px] tracking-[2%] text-brand-primary-800 w-[80%]">
                          {adset.name}
                        </div>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="group-hover:bg-[#f6f6f6] border-0 shadow-none w-9 h-9"
                              size={"icon"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAssetToDuplicate({
                                  campaign: selectedAsset.campaign,
                                  adset: adset,
                                });
                                setOpenDuplicateDialog(true);
                              }}
                            >
                              <Copy size={16} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" align="start" alignOffset={20}>
                            <p>Duplicate Ad set</p>
                          </TooltipContent>
                        </Tooltip>
                      </DropdownMenuItem>
                    ))}
                </div>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <DuplicateAdsetDialog
        open={openDuplicateDialog}
        onOpenChange={setOpenDuplicateDialog}
        duplicatingAsset={selectedAssetToDuplicate}
      />
    </TooltipProvider>
  );
};

export default PublishAction;

interface DuplicateAdsetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicatingAsset: {
    campaign: CampaignAsset | null;
    adset: AdsetAsset | null;
  };
}

const DuplicateAdsetDialog = ({
  open,
  onOpenChange,
  duplicatingAsset,
}: DuplicateAdsetDialogProps) => {
  const [campaignName, setCampaignName] = useState<string>("");
  const [adsetName, setAdsetName] = useState<string>("");
  const setAccountAssets = useCreativeLibraryStore((state) => state.setAccountAssets);
  const accountAssets = useCreativeLibraryStore((state) => state.accountAssets);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);

  const { accountId } = useParams();

  useEffect(() => {
    setCampaignName(
      duplicatingAsset?.campaign?.name ? `${duplicatingAsset.campaign.name}_COPY` : "",
    );
    setAdsetName(
      duplicatingAsset?.adset?.name ? `${duplicatingAsset.adset.name}_COPY` : "",
    );
  }, [duplicatingAsset]);

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      if (!duplicatingAsset.adset?.id || !accountId) return;
      const res = await duplicateAdset(accountId, {
        adset_id: duplicatingAsset.adset?.id,
        adset_name: adsetName,
        campaign_name: campaignName,
      });
      if (res.status === "failed") {
        toast.message(
          <ToastContent
            icon={LuTriangleAlert}
            headingPrefix="Failed to duplicate adset"
            subheadingSuffix={
              res?.message ?? "An error occurred while duplicating. Please try again."
            }
            isError={true}
          />,
        );
        return;
      }

      const duplicatedAdset: AdsetAsset = {
        id: res.data.adset_id,
        name: res.data.adset_name,
        campaign_id: res.data.campaign_id,
      };
      const duplicatedCampaign: CampaignAsset = {
        id: res.data.campaign_id,
        name: res.data.campaign_name,
      };
      setAccountAssets({
        campaigns: [duplicatedCampaign, ...(accountAssets?.campaigns || [])],
        adsets: [duplicatedAdset, ...(accountAssets?.adsets || [])],
      });
      toast.message(
        <ToastContent icon={LuCheck} headingPrefix="Adset duplicated successfully" />,
      );
      onOpenChange(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsDuplicating(false);
    }
  };

  const isFormValid = campaignName.trim() !== "" && adsetName.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px] !rounded-[12px]" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="text-[15px] font-medium leading-[18px] tracking-[0.02em] text-brand-primary-new mb-1">
            Duplicate to New campaign
          </DialogTitle>
          <p className="text-[14px] font-normal leading-[18px] tracking-[0.02em] text-[#02185999] mt-1">
            1 copy of 1 ad set will be duplicated into 1 campaign
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-2 my-2">
          <div className="flex items-center border-[1px] border-border-gray rounded-[8px]">
            <Label
              htmlFor="campaign"
              className="pl-2 text-[12px] font-normal leading-[20px] text-brand-primary-400"
            >
              Campaign
            </Label>
            <Input
              id="campaign"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              className="h-9 border-0 text-brand-primary-800 text-[12px] font-normal"
            />
          </div>
          <div className="flex items-center border-[1px] border-border-gray rounded-[8px]">
            <Label
              htmlFor="adset"
              className="pl-2 text-[12px] font-normal leading-[20px] text-brand-primary-400"
            >
              Adset
            </Label>
            <Input
              id="adset"
              value={adsetName}
              onChange={(e) => setAdsetName(e.target.value)}
              className="h-9 border-0 text-brand-primary-800 text-[12px] font-normal"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              onOpenChange(false);
            }}
            className=""
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              void handleDuplicate();
            }}
            disabled={!isFormValid || isDuplicating}
            className={cn(
              "font-medium bg-[#FF4500] hover:bg-[#FF4500]/90 text-white border-0",
              (!isFormValid || isDuplicating) && "opacity-50 cursor-not-allowed",
            )}
          >
            {isDuplicating ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner className="w-4 h-4" />
                Duplicating...
              </div>
            ) : (
              "Duplicate"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
